import { useCallback, useMemo } from 'react';
import { UAParser } from 'ua-parser-js';
import type { CartItem, CartItemSource, CartItemSourceMeta } from '../../types/cart';
import { QuantityControl } from './QuantityControl';
import { formatPrice } from '../../utils/formatPrice';

interface CartItemCardProps {
  item: CartItem;
  onRemove: (id: number, formatId: number) => void;
  onUpdateQuantity: (id: number, formatId: number, quantity: number) => void;
  isUpdating: boolean;
  discountPercentage: number | null;
  giftQuantity?: number;
}

function DeleteIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function formatRelativeTime(isoDate: string): string {
  const diff = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
  if (diff < 1) return 'hace un momento';
  if (diff < 60) return `hace ${diff} minuto${diff > 1 ? 's' : ''}`;
  if (diff < 1440) {
    const h = Math.floor(diff / 60);
    return `hace ${h} hora${h > 1 ? 's' : ''}`;
  }
  const d = Math.floor(diff / 1440);
  return `hace ${d} día${d > 1 ? 's' : ''}`;
}

function getSourceLabel(source?: CartItemSource, meta?: CartItemSourceMeta): string | null {
  switch (source) {
    case 'pack':
      return 'Agregado desde un pack';
    case 'solution':
      return 'Agregado desde una solución';
    case 'suggested_cart':
      return 'Agregado desde carrito sugerido';
    case 'shared_cart':
      return 'Agregado desde carrito compartido';
    case 'smart_list':
      return 'Agregado desde lista inteligente';
    case 'repeat_order':
      return 'Agregado desde pedido anterior';
    case 'replacement_credit':
      return 'Gratis (reposicion)';
    case 'referral_gift':
      return null; // handled separately with special styling
    case 'saved_cart': {
      let label = 'De tu sesión anterior';
      if (meta?.user_agent) {
        const parser = new UAParser(meta.user_agent);
        const browser = parser.getBrowser().name || '';
        const os = parser.getOS().name || '';
        const device = [browser, os].filter(Boolean).join(' en ');
        if (device) label += ` (${device}`;
        if (meta.saved_at) label += `, ${formatRelativeTime(meta.saved_at)}`;
        if (device) label += ')';
      } else if (meta?.saved_at) {
        label += ` (${formatRelativeTime(meta.saved_at)})`;
      }
      return label;
    }
    default:
      return null;
  }
}

function DiscountBadge({ percentage }: { percentage: number }) {
  // A 100% discount always carries a cap, so "-100%" would be misleading
  // (the item isn't actually free). Show just the icon to flag the item.
  const showPercentage = percentage < 100;
  return (
    <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-[11px] font-semibold px-1.5 py-0.5 rounded">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
      {showPercentage && `-${percentage}%`}
    </span>
  );
}

export function CartItemCard({ item, onRemove, onUpdateQuantity, isUpdating, discountPercentage, giftQuantity }: CartItemCardProps) {
  const handleRemove = useCallback(() => {
    onRemove(item.id, item.format_id);
  }, [item.id, item.format_id, onRemove]);

  const handleQuantityChange = useCallback((quantity: number) => {
    onUpdateQuantity(item.id, item.format_id, quantity);
  }, [item.id, item.format_id, onUpdateQuantity]);

  const isGift = item.source === 'referral_gift' && giftQuantity != null;
  const freeUnits = isGift ? Math.min(giftQuantity, item.quantity) : 0;
  const paidUnits = isGift ? Math.max(0, item.quantity - giftQuantity) : item.quantity;
  const giftTotal = isGift ? paidUnits * item.price : item.total;

  const sourceLabel = useMemo(
    () => getSourceLabel(item.source, item.source_meta),
    [item.source, item.source_meta],
  );

  const formatInfo = (
    <>
      <span className="text-gray-600">{item.format_name}</span>
      {item.format_content && item.format_quantity !== 1 && (
        <span className="text-gray-400">({item.format_content})</span>
      )}
      {isGift ? (
        <>
          <span className="text-gray-400 line-through text-[12px]">{formatPrice(item.price)}</span>
          <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-[11px] font-semibold px-1.5 py-0.5 rounded">
            {freeUnits} GRATIS
          </span>
        </>
      ) : (
        <>
          <span className="text-green-600 font-medium">
            {item.price === 0 ? 'De regalo!' : formatPrice(item.price)}
          </span>
          {discountPercentage && <DiscountBadge percentage={discountPercentage} />}
        </>
      )}
    </>
  );

  const removeButton = (
    <button
      type="button"
      className="flex items-center justify-center w-9 h-9 border-none bg-gray-100 rounded-lg text-gray-400 cursor-pointer transition-all duration-150 shrink-0 hover:enabled:bg-red-50 hover:enabled:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
      onClick={handleRemove}
      disabled={isUpdating}
      title="Eliminar producto"
    >
      <DeleteIcon />
    </button>
  );

  return (
    <div className={`transition-opacity duration-200 ${isGift ? 'bg-amber-50' : ''} ${isUpdating ? 'opacity-90' : ''}`}>
      {/* Syncing indicator - subtle pulse on the right side */}
      {isUpdating && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        </div>
      )}
      {/* Mobile Layout */}
      <div className="block md:hidden p-3.5 relative">
        <div className="flex gap-3 mb-2.5">
          <div className="w-11 h-11 rounded-lg overflow-hidden bg-white border border-gray-200 shrink-0">
            {item.image_url ? (
              <img src={item.image_url} alt="" className="w-full h-full object-contain" loading="lazy" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></svg>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-[14px] font-semibold text-gray-800 m-0 mb-0.5 whitespace-nowrap overflow-hidden text-ellipsis">{item.title}</h4>
            <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-gray-500">
              {formatInfo}
            </div>
            {isGift ? (
              <div className="text-[11px] text-amber-600 font-medium mt-0.5">
                Regalo por referido{paidUnits > 0 && ` · ${paidUnits} extra${paidUnits > 1 ? 's' : ''} a ${formatPrice(item.price)} c/u`}
              </div>
            ) : sourceLabel ? (
              <div className="text-[11px] text-gray-400 mt-0.5">{sourceLabel}</div>
            ) : null}
          </div>
        </div>
        <div className="flex justify-between items-center">
          <QuantityControl quantity={item.quantity} onChange={handleQuantityChange} disabled={false} />
          <div className="flex items-center gap-2.5">
            <span className={`text-[15px] font-semibold ${isGift && paidUnits === 0 ? 'text-amber-600' : 'text-gray-800'}`}>
              {isGift && paidUnits === 0 ? '$0' : formatPrice(isGift ? giftTotal : item.total)}
            </span>
            <button
              type="button"
              className="flex items-center justify-center w-7 h-7 border-none bg-transparent rounded-full text-gray-300 cursor-pointer shrink-0 hover:enabled:bg-red-50 hover:enabled:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              onClick={handleRemove}
              disabled={isUpdating}
              title="Eliminar"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex items-center py-3 px-4 gap-3.5 relative">
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-white border border-gray-200 shrink-0">
          {item.image_url ? (
            <img src={item.image_url} alt="" className="w-full h-full object-contain" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></svg>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-[15px] font-semibold text-gray-800 m-0 mb-0.5 whitespace-nowrap overflow-hidden text-ellipsis">{item.title}</h4>
          <div className="flex flex-wrap items-center gap-1.5 text-[13px] text-gray-500">
            {formatInfo}
          </div>
          {isGift ? (
            <div className="text-[11px] text-amber-600 font-medium mt-0.5">
              Regalo por referido{paidUnits > 0 && ` · ${paidUnits} extra${paidUnits > 1 ? 's' : ''} a ${formatPrice(item.price)} c/u`}
            </div>
          ) : sourceLabel ? (
            <div className="text-[11px] text-gray-400 mt-0.5">{sourceLabel}</div>
          ) : null}
        </div>
        <QuantityControl quantity={item.quantity} onChange={handleQuantityChange} disabled={false} />
        <span className={`text-base font-semibold min-w-[90px] text-right ${isGift && paidUnits === 0 ? 'text-amber-600' : 'text-gray-800'}`}>
          {isGift && paidUnits === 0 ? '$0' : formatPrice(isGift ? giftTotal : item.total)}
        </span>
        <button
          type="button"
          className="flex items-center justify-center w-7 h-7 border-none bg-transparent rounded-full text-gray-300 cursor-pointer shrink-0 hover:enabled:bg-red-50 hover:enabled:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          onClick={handleRemove}
          disabled={isUpdating}
          title="Eliminar"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
